import { useState, useEffect, createContext, useContext } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";

const AIConsentContext = createContext({ hasConsent: false, requestConsent: () => {} });

export function useAIConsent() {
  return useContext(AIConsentContext);
}

export function AIConsentProvider({ children }) {
  const [hasConsent, setHasConsent] = useState(() => {
    return localStorage.getItem("archiva_ai_consent") === "true";
  });
  const [showDialog, setShowDialog] = useState(false);
  const [pendingResolve, setPendingResolve] = useState(null);

  const requestConsent = () => {
    if (hasConsent) return Promise.resolve(true);
    return new Promise((resolve) => {
      setPendingResolve(() => resolve);
      setShowDialog(true);
    });
  };

  const handleAccept = () => {
    localStorage.setItem("archiva_ai_consent", "true");
    setHasConsent(true);
    setShowDialog(false);
    if (pendingResolve) pendingResolve(true);
    setPendingResolve(null);
  };

  const handleDecline = () => {
    setShowDialog(false);
    if (pendingResolve) pendingResolve(false);
    setPendingResolve(null);
  };

  return (
    <AIConsentContext.Provider value={{ hasConsent, requestConsent }}>
      {children}
      <Dialog open={showDialog} onOpenChange={(open) => { if (!open) handleDecline(); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>AI Data Processing</DialogTitle>
            <DialogDescription className="text-left space-y-3 pt-2">
              <p>
                Archiva uses AI to help you analyze, tag, search, and interact with your files.
                To provide these features, some of your data is sent to third-party AI services
                for processing.
              </p>
              <p className="font-semibold text-foreground">What data is shared:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Text extracted from your uploaded documents</li>
                <li>Images you upload (for visual analysis)</li>
                <li>Your chat messages and questions about your files</li>
                <li>Story content (when using AI writing assistance)</li>
              </ul>
              <p className="font-semibold text-foreground">Who receives this data:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>OpenAI</strong> — processes your content for AI-powered tagging,
                  search, chat responses, text-to-speech, and writing assistance</li>
              </ul>
              <p className="font-semibold text-foreground">How your data is protected:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Data is sent securely over encrypted connections (HTTPS)</li>
                <li>OpenAI does not use your data to train their models (API usage)</li>
                <li>Data is processed in real-time and not stored by OpenAI beyond their
                  standard API data retention policy</li>
              </ul>
              <p>
                You can review our full{" "}
                <a
                  href="/privacy-policy.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline"
                >
                  Privacy Policy
                </a>{" "}
                for more details. You can withdraw consent at any time in Settings.
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={handleDecline}>
              Decline
            </Button>
            <Button onClick={handleAccept}>
              I Agree — Enable AI Features
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AIConsentContext.Provider>
  );
}
